import Notifications, { INotificationWithId } from "../../../utils/notifications";
import { CoachingTranslationDictionary } from "../helpers/translations";
import { navigateToCoachingSession } from "../navigation";

const COACHING_SESSION_NOTIFICATION_KEY = "COACHING_SESSION";
const COMPLETED_SESSION = "COMPLETED_SESSION";
const NO_PERMISSION = "NO_PERMISSION";
const COMPLETION_NO_PERMISSION = "COMPLETION_NO_PERMISSION";

/**
 * Thin per-user wrapper over the Drive app bell/notification center
 * (`utils/notifications`) for the coaching module. `publish` is the parameterized
 * entry point; concrete coaching notifications (e.g. sync failures) will be built
 * on top of it. Keeping the registry access behind this class means the rest of
 * the module never touches the global `Notifications` singleton directly.
 */
export class CoachingNotificationService {
    notifyCommentDroppedDueToNoPermission(userId: string, sessionId: string): void {
        this.publish(userId, {
            id: this.formatNotificationKey(sessionId, NO_PERMISSION),
            message: CoachingTranslationDictionary.CommentDroppedNoPermission,
            type: "app",
            isTranslated: false,
            action: () => navigateToCoachingSession(sessionId)
        });
    }

    notifyCommentDroppedDueToCompletion(userId: string, sessionId: string): void {
        this.publish(userId, {
            id: this.formatNotificationKey(sessionId, COMPLETED_SESSION),
            message: CoachingTranslationDictionary.CommentDroppedSessionCompleted,
            type: "app",
            isTranslated: false,
            action: () => navigateToCoachingSession(sessionId)
        });
    }

    notifySessionCompletionDroppedDueToNoPermission(userId: string, sessionId: string): void {
        this.publish(userId, {
            id: this.formatNotificationKey(sessionId, COMPLETION_NO_PERMISSION),
            message: CoachingTranslationDictionary.CompletionDroppedNoPermission,
            type: "app",
            isTranslated: false,
            action: () => navigateToCoachingSession(sessionId)
        });
    }

    /**
     * Publish (or overwrite) a notification on the bell badge for this user.
     * The `id` is a stable de-dupe key — re-publishing the same id replaces the
     * existing entry rather than stacking a new one.
     */
    publish(userId: string, notification: INotificationWithId): void {
        if (!userId) {
            return;
        }
        const { id, ...rest } = notification;
        Notifications.add(id, rest, userId);
    }

    private formatNotificationKey(sessionId: string, key: string): string {
        return `${ COACHING_SESSION_NOTIFICATION_KEY }.${ sessionId }.${ key }`;
    }
}
